import { parseRetryAfter } from "./throttle";

/**
 * One rule for whether a failed session renewal signs the user out (GH-1913).
 *
 * Both refresh implementations (the shell store's `refreshAccessToken` and
 * `tokenStorage`'s `doRefresh`) used to clear auth on ANY non-OK answer. A
 * 429 from the shared refresh limiter, a 502 during a deploy, or a dropped
 * connection therefore signed everyone out and -- via the reload / unmount --
 * kicked them out of their call.
 *
 * Only an answer that says the session itself is gone is definitive. Anything
 * else is the server or the network having a bad moment: keep the session,
 * wait, and try again.
 */
export type RefreshFailureKind = "transient" | "definitive";

/** 4xx answers that are about timing, not the session: retry them. */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);

/**
 * Definitive: any 4xx except 408/429 -- the session is gone (401 expired or
 * revoked, 400 no refresh cookie, 403 account disabled) or the request itself
 * is wrong (404/405/… from a misrouted endpoint, which retrying would never
 * fix and would leave every tab stuck "reconnecting"). Transient: 408, 429,
 * 5xx, and no answer at all (`status` null = network error).
 */
export function classifyRefreshFailure(status: number | null): RefreshFailureKind {
  if (status !== null && status >= 400 && status < 500 && !RETRYABLE_CLIENT_STATUSES.has(status)) {
    return "definitive";
  }
  return "transient";
}

/** Backoff for a transient failure that names no wait: 2s → 4s → … → 60s. */
export const RENEWAL_BACKOFF_BASE_MS = 2_000;
export const RENEWAL_BACKOFF_CAP_MS = 60_000;
/** Added on top of a stated `Retry-After`, so tabs told the same wait don't
 *  all come back in the same millisecond. Never subtracted: AC4 says no
 *  attempt before the stated wait has passed. */
const RETRY_AFTER_SPREAD_MS = 1_000;
const MIN_STATED_WAIT_MS = 1_000;

/**
 * How long to wait before attempt `attempt + 1`. A stated `Retry-After` is
 * honoured in full with no cap; otherwise exponential backoff with jitter.
 */
export function renewalRetryDelayMs(
  retryAfterSeconds: number | undefined,
  attempt: number,
  random: () => number = Math.random,
): number {
  if (retryAfterSeconds !== undefined) {
    return Math.max(retryAfterSeconds * 1000, MIN_STATED_WAIT_MS) + random() * RETRY_AFTER_SPREAD_MS;
  }
  const base = Math.min(RENEWAL_BACKOFF_BASE_MS * 2 ** Math.max(0, attempt), RENEWAL_BACKOFF_CAP_MS);
  // Equal jitter: somewhere in [0.75, 1.25] × base, still capped.
  return Math.min(base * (0.75 + random() * 0.5), RENEWAL_BACKOFF_CAP_MS);
}

// ---------------------------------------------------------------------------
// "Reconnecting your session…" state, read by the shell's top-bar banner.
// ---------------------------------------------------------------------------

let reconnecting = false;
const reconnectingListeners = new Set<() => void>();

function setReconnecting(next: boolean): void {
  if (reconnecting === next) return;
  reconnecting = next;
  reconnectingListeners.forEach((fn) => fn());
}

export function isSessionReconnecting(): boolean {
  return reconnecting;
}

/** `useSyncExternalStore`-shaped subscribe; returns the unsubscribe. */
export function subscribeSessionReconnecting(listener: () => void): () => void {
  reconnectingListeners.add(listener);
  return () => {
    reconnectingListeners.delete(listener);
  };
}

/**
 * Run one renewal, retrying transient failures until it either succeeds
 * (`true`) or is definitively rejected (`false`).
 *
 * While it is retrying, the returned promise simply stays pending. That is
 * the point: every caller that awaits a refresh -- API requests, the realtime
 * sockets, the auth poller -- already treats `false` as "signed out", and now
 * only ever sees `false` when that is true. A 2-minute outage is a 2-minute
 * wait, not a logout.
 *
 * `onSuccess` stores the new tokens; if it throws (e.g. a 200 with an
 * unreadable body) that counts as a transient failure too.
 */
export async function refreshWithRetry(
  attempt: () => Promise<Response>,
  onSuccess: (response: Response) => Promise<void>,
): Promise<boolean> {
  for (let failures = 0; ; failures += 1) {
    let status: number | null = null;
    let retryAfter: number | undefined;
    try {
      const response = await attempt();
      status = response.status;
      if (response.ok) {
        await onSuccess(response);
        setReconnecting(false);
        return true;
      }
      retryAfter = parseRetryAfter(response.headers?.get?.("Retry-After"));
    } catch (error) {
      console.warn("Session renewal failed, will retry:", error);
    }

    // A 2xx lands here only when onSuccess threw; 2xx is never definitive.
    if (classifyRefreshFailure(status) === "definitive") {
      setReconnecting(false);
      return false;
    }

    setReconnecting(true);
    const delay = renewalRetryDelayMs(retryAfter, failures);
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
}
